import { Controller } from '@hotwired/stimulus'

/**
 * Stimulus-контроллер списка удобств клуба.
 *
 * На первом экране оставляет видимыми удобства, которые помещаются в
 * заданное количество строк: две на десктопе и пять на мобильных экранах.
 * По клику на кнопку раскрывает или сворачивает полный список, обновляет
 * текст кнопки и направление иконки-стрелки.
 *
 * @extends {Controller}
 */
export default class ClubFacilitiesController extends Controller {
  /**
   * Элементы управления и пункты списка удобств.
   *
   * @type {string[]}
   */
  static targets = ['toggle', 'item']

  /**
   * Тексты кнопки для состояний "показать больше" и "показать меньше".
   *
   * @type {{showMore: StringConstructor, showLess: StringConstructor}}
   */
  static values = {
    showMore: String,
    showLess: String
  }

  static DESKTOP_QUERY = '(min-width: 1024px)'
  static DESKTOP_ROWS = 2
  static MOBILE_ROWS = 5
  static ROW_TOP_TOLERANCE = 2

  /**
   * Текущее состояние списка: `true`, если дополнительные удобства скрыты.
   *
   * @type {boolean}
   */
  #collapsed = true

  /**
   * Идентификатор запланированного пересчета видимости.
   *
   * @type {number|null}
   */
  #frameId = null

  /**
   * Наблюдатель за шириной контейнера списка.
   *
   * @type {ResizeObserver|null}
   */
  #resizeObserver = null

  /**
   * Флаг подключения контроллера к DOM.
   *
   * @type {boolean}
   */
  #connected = false

  /**
   * Последняя ширина контейнера списка, для которой выполнялся пересчет.
   *
   * @type {number|null}
   */
  #lastObservedWidth = null

  /**
   * Скрывает удобства, которые выходят за лимит строк при подключении
   * контроллера, и подписывается на изменение ширины списка.
   *
   * @returns {void}
   */
  connect() {
    this.#connected = true
    this.#collapsed = true
    this.#observeListWidth()
    this.#scheduleVisibilityUpdate()

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (!this.#connected) return

        this.#scheduleVisibilityUpdate()
      })
    }
  }

  /**
   * Хук отключения контроллера.
   *
   * Отписывается от наблюдателя и отменяет отложенный пересчет, если он еще
   * не успел выполниться.
   *
   * @returns {void}
   */
  disconnect() {
    this.#connected = false
    this.#resizeObserver?.disconnect()

    if (this.#frameId) {
      cancelAnimationFrame(this.#frameId)
      this.#frameId = null
    }
  }

  /**
   * Переключает видимость дополнительных удобств и текст кнопки.
   *
   * @returns {void}
   */
  toggle() {
    this.#collapsed = !this.#collapsed
    this.#updateVisibility()
  }

  /**
   * Подписывается на изменение ширины списка удобств.
   *
   * @returns {void}
   */
  #observeListWidth() {
    const listElement = this.itemTargets[0]?.parentElement || this.element

    if (!listElement || !window.ResizeObserver) return

    this.#resizeObserver = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width

      if (
        this.#lastObservedWidth === null ||
        Math.abs(width - this.#lastObservedWidth) > 0.5
      ) {
        this.#lastObservedWidth = width
        this.#scheduleVisibilityUpdate()
      }
    })

    this.#resizeObserver.observe(listElement)
  }

  /**
   * Планирует пересчет видимости на следующий animation frame.
   *
   * @returns {void}
   */
  #scheduleVisibilityUpdate() {
    if (this.#frameId) return

    this.#frameId = requestAnimationFrame(() => {
      this.#frameId = null
      this.#updateVisibility()
    })
  }

  /**
   * Обновляет видимость пунктов и состояние кнопки раскрытия.
   *
   * @returns {void}
   */
  #updateVisibility() {
    const visibleCount = this.#calculateVisibleCount()
    const hasHiddenItems = visibleCount < this.itemTargets.length

    this.itemTargets.forEach((item, index) => {
      item.hidden = hasHiddenItems && this.#collapsed && index >= visibleCount
      item.classList.remove('hidden')
    })

    this.#updateToggle(hasHiddenItems)
  }

  /**
   * Считает, сколько пунктов помещается в разрешенное количество строк.
   *
   * @returns {number}
   */
  #calculateVisibleCount() {
    const maxRows = this.#maxRows()
    const rowTops = []
    let visibleCount = 0

    this.itemTargets.forEach((item) => {
      item.hidden = false
      item.classList.remove('hidden')
    })

    this.itemTargets.forEach((item) => {
      const top = item.getBoundingClientRect().top
      let rowIndex = rowTops.findIndex(
        (rowTop) =>
          Math.abs(rowTop - top) <= ClubFacilitiesController.ROW_TOP_TOLERANCE
      )

      if (rowIndex === -1) {
        rowTops.push(top)
        rowIndex = rowTops.length - 1
      }

      if (rowIndex < maxRows) visibleCount += 1
    })

    return visibleCount
  }

  /**
   * Возвращает количество строк для текущей ширины viewport.
   *
   * @returns {number}
   */
  #maxRows() {
    return window.matchMedia(ClubFacilitiesController.DESKTOP_QUERY).matches
      ? ClubFacilitiesController.DESKTOP_ROWS
      : ClubFacilitiesController.MOBILE_ROWS
  }

  /**
   * Обновляет видимость, текст и aria-состояние кнопки раскрытия.
   *
   * @param {boolean} hasHiddenItems Есть ли пункты, которые нужно скрывать.
   * @returns {void}
   */
  #updateToggle(hasHiddenItems) {
    if (!this.hasToggleTarget) return

    this.toggleTarget.hidden = !hasHiddenItems
    this.toggleTarget.setAttribute('aria-expanded', String(!this.#collapsed))

    const linkTextElement = this.toggleTarget.querySelector('.link__text')
    if (linkTextElement) {
      linkTextElement.textContent = this.#collapsed
        ? this.showMoreValue
        : this.showLessValue
    }

    this.#updateArrow()
  }

  /**
   * Поворачивает иконку-стрелку в кнопке согласно текущему состоянию списка.
   *
   * @returns {void}
   */
  #updateArrow() {
    if (!this.hasToggleTarget) return

    const icon = this.toggleTarget.querySelector('svg')
    if (icon) {
      if (this.#collapsed) {
        icon.classList.remove('rotate-180')
      } else {
        icon.classList.add('rotate-180')
      }
    }
  }
}
